import { humanize, labelFor, stepReversibilityLabel, strategyLabel } from '../labels';
import { DeploymentImpactPanel, SpeedCheck, VerificationGroups, increaseLabel } from './DeploymentImpactPanel';
import { SkeletonPage, StatusBadge } from './ui';
import { Callout, Icon, IconButton, LogPanel, Segmented, StepList, Tabs } from './primitives';
import { StageList } from './deploy/StageList';
import { StepError } from './deploy/StepError';
import { DeploymentSummary } from './deploy/DeploymentSummary';
import { assessDeploymentImpact } from './operation/impact';
import { actionLabel } from './operation/actionLabels';
import { placeOfStep } from './deploy/stageNumbering';
import type { LogLine, StepItem } from './primitives';
import { FailureInterpretationPanel } from './FailureInterpretationPanel';
import { RollbackDialog } from './RollbackDialog';
import { commitURL } from './project/commitLink';
import { DatabaseChangesPanel, changesStructure } from './DatabaseChangesPanel';
import { StepTime, formatClock, isRunningStatus, timingOf, timingSentence, useNow } from './runTiming';
import type { SectionPath } from './project/operationalHealth';
import { Initiator, ServingLabel, Timestamp, durationBetween } from './status/StatusPieces';
import { inProgress, outcomeOfStatus } from './status/statusModel';
import { errorCodeOf } from './primitives/logText';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useResource } from '../hooks';
import type { LiveSiteHealth } from '../api';
import { ExecutionLog, getProjectDeployment, startProjectDeployment, getExecutionLogs } from '../api';

type LogFilter = 'all' | 'error' | 'warn' | 'info';
type DetailTab = 'progress' | 'checks' | 'database' | 'log';

const logFilterOptions: { value: LogFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'error', label: 'Errors' },
  { value: 'warn', label: 'Warnings' },
  { value: 'info', label: 'Info' },
];

/** The engine writes both "warn" and "warning"; both are one level here. */
function logLevel(level: string): string {
  const normalized = level.toLowerCase();
  return normalized === 'warn' ? 'warning' : normalized;
}

interface DeploymentTimelineViewProps {
  deploymentId: string;
  onRefresh?: () => void;
  onBackToList?: () => void;
  /**
   * Opened from elsewhere (an incident's suggested action) to do one thing:
   * show the log, or open the rollback dialog — the real one, gated by the
   * server's rollback decision. Handled once, then reported back.
   */
  initialAction?: 'rollback' | 'log';
  onInitialActionHandled?: () => void;
  /** Turns a failure interpretation's targets into links (the project's sections). */
  sectionPath?: SectionPath;
  /** Names the environment in the rollback confirmation and the summary. */
  environmentName?: string;
  /** The repository of a deployed component, to link its commit (CO-100). */
  repositoryOf?: (applicationId: string | null | undefined) => string | undefined;
  /** The environment's live site now, for "what this meant for visitors" (CO-150). */
  site?: LiveSiteHealth;
}

/**
 * DeploymentTimelineView is one deployment (CO-150), organised around its
 * outcome and progress:
 *
 * 1. The release: which component, which change, the outcome of the
 *    attempt and whether its release serves now, when and how long, who.
 * 2. What happened: a running deployment's current stage and whether
 *    traffic has switched; a failure's structured summary (what failed and
 *    where, visitors, what serves, likely cause and how sure, next step);
 *    a success's checks.
 * 3. The evidence, in tabs: the stages and steps (numbered inside their
 *    stage), the verification checks, the database change, the log.
 *
 * "View error" opens the failed step in place and marks it; "Full logs"
 * opens the log searched for the error.
 */
export const DeploymentTimelineView: React.FC<DeploymentTimelineViewProps> = ({
  deploymentId,
  onRefresh,
  onBackToList,
  initialAction,
  onInitialActionHandled,
  sectionPath,
  environmentName,
  repositoryOf,
  site,
}) => {
  // Cached by deployment: opening one already seen shows it at once, and a
  // poll that finds nothing new changes nothing on screen.
  const detail = useResource(() => getProjectDeployment(deploymentId), [deploymentId], undefined, { key: `deployment:${deploymentId}` });
  const data = detail.data;
  const initialActionDone = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rollbackRefusal, setRollbackRefusal] = useState<string | null>(null);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const loading = detail.loading;
  const [actionError, setError] = useState<string | null>(null);
  const error = actionError ?? detail.error?.message ?? null;
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [logQuery, setLogQuery] = useState('');
  const [targetStep, setTargetStep] = useState<string | null>(searchParams.get('step'));
  const [showRollbackModal, setShowRollbackModal] = useState<boolean>(false);
  const requestedTab = searchParams.get('tab') as DetailTab | null;
  const tab: DetailTab = requestedTab && ['progress', 'checks', 'database', 'log'].includes(requestedTab) ? requestedTab : 'progress';
  const tabsRef = useRef<HTMLDivElement>(null);
  // Live counters tick only while the deployment runs.
  const now = useNow(Boolean(data && (isRunningStatus(data.deployment.status) || data.steps.some((step) => step.status === 'running'))));

  const setTab = (next: DetailTab, extra: Record<string, string | null> = {}) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'progress') params.delete('tab');
    else params.set('tab', next);
    for (const [key, value] of Object.entries(extra)) {
      if (value === null) params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  };

  const reloadDetail = detail.reload;
  const loadData = async () => {
    setError(null);
    reloadDetail();
  };

  const loadLogs = async () => {
    if (!data?.execution?.id) return;
    try {
      const resp = await getExecutionLogs(data.execution.id);
      setLogs(resp.logs || []);
    } catch {
      // Non-fatal
    }
  };

  // Progress is read every 2 s while the tab is in front (live events refresh
  // it sooner), and the logs with it while they are open.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (data && !inProgress(outcomeOfStatus(data.deployment.status, data.deployment.failure_reason)) && data.execution?.status !== 'running') return;
      reloadDetail();
      if (tab === 'log') loadLogs();
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, tab, reloadDetail, data?.deployment.status]);

  useEffect(() => {
    if (tab === 'log') loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, data?.execution?.id]);

  // An incident's suggested action lands here with one intent. The rollback
  // dialog opens only when the server allows the rollback; otherwise the
  // server's reason is shown instead — never a dialog that would be refused.
  useEffect(() => {
    if (!data || !initialAction || initialActionDone.current) return;
    initialActionDone.current = true;
    if (initialAction === 'log') {
      // One URL update: a second one built from the same params (clearing
      // the intent) would drop the tab again.
      setTab('log', { intent: null });
      return;
    }
    if (initialAction === 'rollback') {
      if (data.rollback_eligibility?.allowed) setShowRollbackModal(true);
      else setRollbackRefusal(data.rollback_eligibility?.reason ?? 'Commerce Ops could not decide whether this deployment can be rolled back.');
    }
    onInitialActionHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, initialAction, onInitialActionHandled]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await startProjectDeployment(deploymentId);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to start deployment');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !data) {
    return <SkeletonPage rows={4} />;
  }

  if (error && !data) {
    return (
      <Callout
        tone="danger"
        title="Unable to load deployment"
        actions={
          <button type="button" className="button button--secondary button--small" onClick={loadData}>
            Retry
          </button>
        }
      >
        {error}
      </Callout>
    );
  }

  if (!data) return null;

  const { deployment, release, execution, steps, verifications, rollback, controls } = data;
  const stages = data.stages ?? [];
  const rehearsal = execution?.mode === 'rehearsal';
  const dbChanges = data.database_changes;
  const latency = data.latency;
  const outcome = data.serving?.outcome ?? outcomeOfStatus(deployment.status, deployment.failure_reason);
  const serving = data.serving?.serving ?? (inProgress(outcome) ? 'pending' : 'unknown');
  const running = inProgress(outcome);
  const runStart = execution?.started_at ?? deployment.started_at ?? deployment.created_at;
  const runElapsed = Math.max(0, now - Date.parse(runStart));
  const component = deployment.component_name || (deployment.scope === 'combined' ? 'Magento and frontend' : 'Magento');
  const sha = deployment.scope === 'frontend_only' && deployment.frontend_commit_sha ? deployment.frontend_commit_sha : deployment.source_commit_sha;
  const message = (deployment.source_commit_message || deployment.frontend_commit_message || '').split('\n')[0];
  const commitHref = commitURL(repositoryOf?.(deployment.application_id), sha);
  const failedKey = data.interpretation?.failed_step?.key ?? null;
  const place = failedKey ? placeOfStep(failedKey, stages, steps) : null;
  const failedStep = failedKey ? steps.find((step) => step.step_key === failedKey) : undefined;
  const errorCode = errorCodeOf(failedStep?.error_message ?? deployment.failure_reason ?? '');
  const impact = assessDeploymentImpact({ outcome, serving, stages, databaseApplied: dbChanges?.applied, site, rehearsal });
  const waiting =
    (deployment.status as string) === 'awaiting_db_approval'
      ? ('database_approval' as const)
      : execution?.status === 'paused_waiting_for_agent'
        ? ('agent_offline' as const)
        : execution?.status === 'waiting_for_input'
          ? ('input' as const)
          : null;
  // The failure summary carries its own View error and Full logs: the header
  // does not repeat the second one.
  const failureSummaryShown = outcome === 'failed' && !rehearsal && (!data.interpretation || data.interpretation.kind === 'deployment');
  const currentStage = stages.find((stage) => stage.status === 'running');
  const dbBadge = rehearsal || dbChanges?.state === 'pending'
    ? { kind: 'dbChange' as const, value: 'unknown', label: rehearsal ? undefined : 'Not analysed yet' }
    : dbChanges?.overall
      ? { kind: 'dbOverall' as const, value: dbChanges.overall, label: undefined }
      : dbChanges?.state === 'analysed' && !changesStructure(dbChanges.status) && deployment.db_change_classification === 'db_change_detected'
        ? { kind: 'dbChange' as const, value: 'db_change_backward_compatible', label: 'Data patches only' }
        : { kind: 'dbChange' as const, value: deployment.db_change_classification, label: undefined };

  // A verdict Magento or the release comparison gave, or — while the run
  // is going — the honest "not analysed yet". An unknown that is over is not
  // a fact worth a place in the header.
  const databaseKnown = running || (dbBadge.kind === 'dbOverall' && dbBadge.value !== 'pending' && dbBadge.value !== 'undetermined');

  const filteredLogs = logs.filter((l) => (logFilter === 'all' ? true : logLevel(l.level) === logLevel(logFilter)));
  const logLines: LogLine[] = filteredLogs.map((log) => ({
    key: log.id,
    at: new Date(log.timestamp).toLocaleTimeString(),
    level: logLevel(log.level),
    source: log.source || undefined,
    text: log.message,
  }));

  const stepItems: StepItem[] = steps.map((step) => ({
    key: step.id,
    title: step.title,
    description: step.description,
    status: { kind: 'execution', value: step.status },
    meta: <StepTime step={step} now={now} />,
    error: step.error_message ? <StepError message={step.error_message} code={step.error_code} /> : undefined,
    details: (
      <>
        <div className="type-meta" data-testid="step-timing-detail">
          {timingSentence(timingOf(step, now))}
          {step.attempt_count > 1 ? ` · ${step.attempt_count} attempts` : ''}
          {` · step ${step.sequence} of ${steps.length} in the run`}
        </div>
        <div className="type-meta">
          {labelFor(stepReversibilityLabel, step.reversibility)} · <code>{step.command_name}</code>
        </div>
        {step.result ? (
          <details className="disclosure">
            <summary>Output</summary>
            <pre className="step-list__output">{JSON.stringify(step.result, null, 2)}</pre>
          </details>
        ) : null}
      </>
    ),
    open: step.status === 'failed',
    target: targetStep === step.step_key,
    testId: `deployment-step-${step.step_key}`,
  }));
  const stepItemByKey = new Map(steps.map((step, index) => [step.step_key, stepItems[index]]));

  const failedStepKey = failedKey ?? steps.find((step) => step.status === 'failed')?.step_key ?? null;
  const showStep = () => {
    if (!failedStepKey) return;
    setTargetStep(null);
    setTab('progress', { step: failedStepKey });
    // A fresh target each time, so pressing it again scrolls again.
    window.setTimeout(() => setTargetStep(failedStepKey), 0);
  };
  const showLog = (atError = true) => {
    setLogQuery(atError ? (errorCode ?? '') : '');
    setLogFilter('all');
    setTab('log');
    tabsRef.current?.scrollIntoView?.({ block: 'start' });
  };

  const checks = verifications ?? [];
  const failedChecks = checks.filter((check) => check.status === 'failed').length;
  const tabOptions: { value: DetailTab; label: string; count?: number; testId?: string }[] = [
    { value: 'progress', label: stages.length > 0 ? 'Stages' : 'Steps', testId: 'detail-tab-progress' },
    { value: 'checks', label: 'Checks', count: checks.length > 0 ? checks.length : undefined, testId: 'detail-tab-checks' },
    { value: 'database', label: 'Database', testId: 'detail-tab-database' },
    { value: 'log', label: actionLabel.fullLogs, testId: 'detail-tab-log' },
  ];

  return (
    <div className="deploy-detail" data-testid="deployment-detail">
      <section className="panel release-summary" aria-labelledby="release-summary-title">
        <div className="release-summary__top">
          {onBackToList ? (
            <button type="button" className="button button--ghost button--small release-summary__back" onClick={onBackToList} aria-label="Back to deployments list">
              <Icon name="back" size={16} />
              Deployments
            </button>
          ) : null}
          <div className="release-summary__actions">
            {controls.can_start && deployment.status === 'pending' ? (
              <button type="button" className="button button--primary" onClick={handleStart} disabled={actionLoading}>
                {actionLoading ? 'Starting' : 'Start deployment'}
              </button>
            ) : null}
            {data.rollback_eligibility?.allowed ? (
              <button type="button" className="button button--danger-quiet" onClick={() => setShowRollbackModal(true)} disabled={actionLoading}>
                Roll back
              </button>
            ) : null}
            {failureSummaryShown ? null : (
              <button type="button" className="button button--secondary" onClick={() => (tab === 'log' ? setTab('progress') : showLog(false))}>
                {tab === 'log' ? 'Hide full logs' : actionLabel.fullLogs}
              </button>
            )}
          </div>
        </div>
        <p className="release-summary__context">
          {component}
          {environmentName ? <> · {environmentName}</> : null}
        </p>
        <h2 id="release-summary-title" className="release-summary__title">
          {message || <>Commit <code translate="no">{sha.slice(0, 7)}</code></>}
        </h2>
        <dl className="release-summary__facts">
          <div>
            <dt>Commit</dt>
            <dd>
              {commitHref ? (
                <a href={commitHref} target="_blank" rel="noreferrer">
                  <code translate="no">{sha.slice(0, 10)}</code>
                  <span className="visually-hidden"> (opens the repository in a new tab)</span>
                </a>
              ) : (
                <code translate="no">{sha.slice(0, 10)}</code>
              )}
              {deployment.source_branch ? <> on {deployment.source_branch}</> : null}
            </dd>
          </div>
          <div>
            <dt>Started by</dt>
            <dd>
              <Initiator value={deployment.created_by} />
            </dd>
          </div>
          <div>
            <dt>Started</dt>
            <dd>
              <Timestamp iso={deployment.created_at} />
            </dd>
          </div>
          <div data-testid="run-timing">
            <dt>{running ? 'Running for' : 'Took'}</dt>
            <dd className="tabular-nums">
              {running ? (
                // A live counter reads like the running step's: "4:12".
                <time dateTime={`PT${Math.round(runElapsed / 1000)}S`}>{formatClock(runElapsed)}</time>
              ) : (
                durationBetween(runStart, execution?.completed_at ?? deployment.completed_at, now) || 'Not started'
              )}
            </dd>
          </div>
          <div>
            <dt>Release</dt>
            <dd>
              <ServingLabel serving={serving} testId="detail-serving" />
            </dd>
          </div>
          {databaseKnown ? (
            // Only a database verdict Magento or the release comparison gave:
            // "not analysed" is not a fact worth a place in the header.
            <div>
              <dt>Database</dt>
              <dd>
                <StatusBadge kind={dbBadge.kind} value={dbBadge.value} label={dbBadge.label} variant="plain" />
              </dd>
            </div>
          ) : null}
        </dl>
        <details className="disclosure release-summary__more">
          <summary>Release details</summary>
          <dl className="release-summary__facts release-summary__facts--plain">
            <div>
              <dt>Release name</dt>
              <dd className="release-summary__release">
                <code translate="no" title={release.release_tag}>
                  {release.release_tag}
                </code>
                <IconButton
                  icon="copy"
                  label="Copy the release name"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    void navigator.clipboard?.writeText(release.release_tag);
                  }}
                />
              </dd>
            </div>
            <div>
              <dt>Strategy</dt>
              <dd>{strategyLabel[deployment.deployment_strategy as keyof typeof strategyLabel] ?? humanize(deployment.deployment_strategy)}</dd>
            </div>
            {databaseKnown ? null : (
              <div>
                <dt>Database change</dt>
                <dd>
                  <StatusBadge kind={dbBadge.kind} value={dbBadge.value} label={dbBadge.label} variant="plain" />
                </dd>
              </div>
            )}
            {execution?.id && sectionPath ? (
              <div>
                <dt>Run on the server</dt>
                <dd>
                  <Link to={sectionPath('deployments', { run: execution.id, view: 'runs' })}>
                    <code translate="no">{execution.id.slice(0, 8)}</code> {actionLabel.fullLogs.toLowerCase()} and controls
                  </Link>
                </dd>
              </div>
            ) : null}
          </dl>
          {deployment.impact ? (
            <>
              <p className="release-summary__impact-line">{deployment.impact.summary || 'What this deployment affects'}</p>
              <DeploymentImpactPanel impact={deployment.impact} />
            </>
          ) : null}
        </details>
      </section>

      {rollbackRefusal ? (
        <Callout tone="warning" role="status" title="This deployment cannot be rolled back" testId="rollback-refused">
          {rollbackRefusal}
          {data.checkpoint && sectionPath && data.rollback_eligibility?.database_safety !== 'safe' ? (
            <>
              {' '}
              <Link to={sectionPath('recovery', { intent: 'test_restore', point: data.checkpoint.id })}>Open the backup taken before this deployment</Link>
            </>
          ) : null}
        </Callout>
      ) : null}

      {data.interpretation && data.interpretation.kind === 'rollback' ? (
        // A rollback on request is told by its own panel (CO-140).
        <FailureInterpretationPanel interpretation={data.interpretation} sectionPath={sectionPath} onShowStep={() => showStep()} />
      ) : rehearsal || (data.interpretation && data.interpretation.kind === 'rehearsal') ? (
        <Callout tone="info" title="Rehearsal">
          Every step was recorded as it would run; no server was touched and no release was activated. Deploy again without the rehearsal option to run it for real.
        </Callout>
      ) : data.interpretation && data.interpretation.kind !== 'deployment' ? (
        <FailureInterpretationPanel interpretation={data.interpretation} sectionPath={sectionPath} onShowStep={() => showStep()} />
      ) : running && !waiting ? null : (
        // A deployment that is progressing is told by the project header's
        // bar and the stages below; its summary returns once it ends, or
        // while it waits for someone.
        <DeploymentSummary
          outcome={outcome}
          serving={serving}
          impact={impact}
          stages={stages}
          site={site}
          interpretation={data.interpretation}
          place={place}
          failureReason={deployment.failure_reason}
          waiting={running ? waiting : null}
          liveRelease={data.live_release}
          currentStep={currentStage?.current_step}
          checks={{ total: checks.length, failed: failedChecks }}
          sectionPath={sectionPath}
          onViewError={failedKey || stages.some((stage) => stage.status === 'failed') ? showStep : undefined}
          onFullLogs={() => showLog()}
          onViewChecks={checks.length > 0 ? () => setTab('checks') : undefined}
        />
      )}

      {/* A rollback runs as its own run (RES-018): in progress, failed (the
          deployment is left as it was) or done. */}
      {rollback && rollback.code_rollback_status === 'in_progress' && (
        <Callout tone="info" title="Rolling back" testId="rolling-back" role="status">
          <p>
            Switching back to the earlier release, started by <strong>{rollback.initiated_by}</strong>. The environment is locked until it finishes.
          </p>
          {sectionPath ? (
            <p>
              <Link to={sectionPath('deployments', { run: rollback.execution_id })}>Follow the rollback step by step</Link>
            </p>
          ) : null}
        </Callout>
      )}
      {rollback && (rollback.code_rollback_status ?? '').startsWith('failed') && (
        <Callout tone="danger" title="The rollback failed" testId="rollback-failed">
          <p>
            {(rollback.code_rollback_status ?? '').replace(/^failed:\s*/, '')}. This deployment is still the live one; nothing was recorded as rolled back. You can try again.
          </p>
          {sectionPath ? (
            <p>
              <Link to={sectionPath('deployments', { run: rollback.execution_id })}>Open the rollback run</Link>
            </p>
          ) : null}
        </Callout>
      )}
      {/* A rollback on request is told by the interpretation above (CO-140). */}
      {rollback && rollback.code_rollback_status === 'completed' && data.interpretation?.kind !== 'rollback' && (
        <Callout tone="warning" title="Rolled back" testId="rolled-back">
          {data.live_release ? (
            <p data-testid="rolled-back-live">
              <strong>Live now:</strong> <code>{data.live_release.commit_sha.slice(0, 7)}</code> {data.live_release.commit_message?.split('\n')[0]} ({data.live_release.release_tag})
            </p>
          ) : null}
          <p>
            Initiated by <strong>{rollback.initiated_by}</strong> on {new Date(rollback.created_at).toLocaleString()}
          </p>
          <p>
            <strong>Reason:</strong> {rollback.reason}
          </p>
          <p>
            Code: {humanize(rollback.code_rollback_status)} · Database: {humanize(rollback.db_rollback_status)}
          </p>
        </Callout>
      )}

      {latency?.state === 'slower' ? (
        <Callout
          tone="warning"
          title="Storefront slower since this release"
          testId="speed-slower"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => setTab('checks')}>
              View checks
            </button>
          }
        >
          {latency.before.median_ms} ms → {latency.after.median_ms} ms ({increaseLabel(latency)})
        </Callout>
      ) : null}

      <section className="panel deploy-detail__evidence" aria-label="Deployment evidence" ref={tabsRef}>
        <Tabs label="Deployment detail" variant="secondary" idBase="deployment-detail-tab" panelId="deployment-detail-panel" value={tab} onChange={(next) => setTab(next)} options={tabOptions} />
        <div role="tabpanel" id="deployment-detail-panel" aria-labelledby={`deployment-detail-tab-${tab}`} className="view-switch__panel">
          {tab === 'progress' ? (
            steps.length === 0 ? (
              <p className="panel__note muted">No steps yet.</p>
            ) : stages.length > 0 ? (
              <>
                <StageList stages={stages} stepItem={(key) => stepItemByKey.get(key)} now={now} label="Deployment stages" testIdPrefix="deployment-stage" targetKey={targetStep} outcome={outcome} />
              </>
            ) : (
              <StepList steps={stepItems} label="Deployment steps" />
            )
          ) : tab === 'checks' ? (
            <>
              {latency ? <SpeedCheck latency={latency} /> : null}
              {checks.length > 0 ? (
                <VerificationGroups verifications={checks} />
              ) : latency ? null : (
                <p className="panel__note muted">{running ? 'Pending' : 'None ran'}</p>
              )}
            </>
          ) : tab === 'database' ? (
            !rehearsal && dbChanges ? (
              <DatabaseChangesPanel changes={dbChanges} />
            ) : rehearsal ? (
              <p className="panel__note muted">Not analysed (rehearsal)</p>
            ) : deployment.db_change_classification === 'no_db_change' ? (
              <p className="panel__note">This release was analysed as not changing the database.</p>
            ) : (
              <p className="panel__note">
                <strong>Rolling back code does not roll back the database.</strong> Switching to the previous release does not undo schema changes this release made. Commerce Ops takes a
                database checkpoint before any upgrade runs, so a restore is possible.
              </p>
            )
          ) : (
            <LogPanel
              title="Full logs"
              lines={logLines}
              empty="No logs recorded yet."
              live={execution.status === 'running'}
              testId="deployment-log"
              initialQuery={logQuery}
              toolbar={<Segmented label="Log level" size="sm" options={logFilterOptions} value={logFilter} onChange={setLogFilter} />}
            />
          )}
        </div>
      </section>

      {showRollbackModal ? (
        <RollbackDialog
          deploymentId={deploymentId}
          environmentName={environmentName ?? 'this environment'}
          sectionPath={sectionPath}
          onClose={() => setShowRollbackModal(false)}
          onRolledBack={() => {
            setShowRollbackModal(false);
            void loadData();
            if (onRefresh) onRefresh();
          }}
        />
      ) : null}
    </div>
  );
};
